import React, { useEffect, useState, useRef } from "react";
import { Col, Row, ButtonGroup } from "react-bootstrap";
import { setCreateNew, selectCreateNew, selectLastCreateType } from "../../features/ui/uiSlice";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useProjects } from "../../features/projects/projectSlice";
import { useDevConfig } from "../../features/config/configSlice";
import { AppType } from "@common/types/sharedTypes";
import { ng } from "@common/ngds";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { EditIcon } from "@simplex/sx-icons";

import { useTranslation } from "react-i18next";
import { User } from "@auth0/auth0-react";
import { ProjectFile } from "../../ProjectExplorer";
import { useUser } from "../../features/user/userSlice";
import HeaderToolbarActions from "./HeaderToolbarActions";
import HeaderToolbarCreateProjectForm from "./HeaderToolbarCreateProjectForm";

export interface HeaderToolbarProps<T> {
  items?: T[];
  id?: string;
  className?: string;
  headerTitle?: string;
  showStatusDropdown?: boolean;
  statusValue?: string;
  onStatusChange?: (nextStatus: string) => Promise<void> | void;
  disableFileActions?: boolean;
  selectedProjecteName?: string;
  uploadProjectFile?: (files: File) => void;
  isInProjectlist?: boolean;
  isFileList?: boolean;
  buttonTitle?: string;
  handleDelete?: () => void;
  setSelectedProjectName?: (projectName: string | null) => void;
  setCopyOfCurrentSelectedFile?: (file: ProjectFile | null) => void;
  user?: User;
  setSelectedFile?: (file: number) => void;
  beamAccess?: boolean;
  foundationAccess?: boolean;
  columnAccess?: boolean;
  frameAccess?: boolean;
  sectionAccess?: boolean;
  retainingWallAccess?: boolean;
  basementWallAccess?: boolean;
  pileAccess?: boolean;
  cptAccess?: boolean;
  setSelectedFileName: (fileName: string) => void;
  renderToolbarActions?: () => React.ReactNode;
  /** Called when the user commits an edit of `headerTitle` while `isFileList` is true. */
  onRenameProject?: (newName: string) => Promise<void> | void;
}

function HeaderToolbar<T>({
  items,
  id,
  className,
  headerTitle,
  showStatusDropdown,
  statusValue,
  onStatusChange,
  disableFileActions,
  buttonTitle,
  uploadProjectFile,
  isInProjectlist,
  isFileList,
  handleDelete,
  setSelectedProjectName,
  setCopyOfCurrentSelectedFile,
  selectedProjecteName,
  user,
  setSelectedFileName,
  setSelectedFile,
  beamAccess,
  foundationAccess,
  columnAccess,
  frameAccess,
  sectionAccess,
  retainingWallAccess,
  basementWallAccess,
  pileAccess,
  cptAccess,
  renderToolbarActions,
  onRenameProject,
}: HeaderToolbarProps<T>) {
  const { t } = useTranslation();
  const showCreateNew = useAppSelector(selectCreateNew);
  const lastCreateType = useAppSelector(selectLastCreateType);
  const [showCreateNewProject, setShowCreateNewProject] = useState<Boolean>(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const dispatch = useAppDispatch();
  const [projectName, setProjectName] = useState<string>("");
  const projectNameInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [template, setTemplate] = useState<AppType | null>(null);
  const [mtrlTemplate, setMtrlTemplate] = useState<ng.material.Mtrl | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const { createNewProject } = useProjects();
  const { getUser } = useUser();
  const isDevConfig = useDevConfig();

  const resetCreateProjectForm = () => {
    setFileName("");
    setProjectName("");
    setTemplate(null);
    setMtrlTemplate(null);
    setShowCreateNewProject(false);
  };

  const isDuplicateProjectName =
    !!user &&
    !!user.projectdigest &&
    Object.keys(user.projectdigest).findIndex((project) => project === projectName) > -1;

  const handleToggleCreate = () => {
    if (disableFileActions) return;

    const nextValue = showCreateNew ? false : true;
    setShowCreateNewProject(nextValue);

    if (!isInProjectlist) {
      // Reopen on the type the user picked last time, if they still have access to it. Otherwise
      // pick their only app, or Beam, or the first app they do have access to.
      const appAccess: Array<[boolean | undefined, AppType]> = [
        [beamAccess, AppType.SIMPLEX_APP_BEAM],
        [foundationAccess, AppType.SIMPLEX_APP_FOUNDATION],
        [columnAccess, AppType.SIMPLEX_APP_COLUMN],
        [frameAccess, AppType.SIMPLEX_APP_FRAME],
        [sectionAccess, AppType.SIMPLEX_APP_SECTION],
        [retainingWallAccess, AppType.SIMPLEX_APP_RETAININGWALL],
        [basementWallAccess, AppType.SIMPLEX_APP_BASEMENT_WALL],
        [pileAccess, AppType.SIMPLEX_APP_PILE],
        [cptAccess, AppType.SIMPLEX_APP_CPT],
      ];
      const accessible = appAccess.filter(([hasAccess]) => hasAccess);

      if (!nextValue || accessible.length === 0) {
        dispatch(setCreateNew(null));
      } else if (accessible.length === 1) {
        dispatch(setCreateNew(accessible[0][1]));
      } else if (lastCreateType !== null && accessible.some(([, type]) => type === lastCreateType)) {
        dispatch(setCreateNew(lastCreateType));
      } else if (beamAccess) {
        dispatch(setCreateNew(AppType.SIMPLEX_APP_BEAM));
      } else {
        dispatch(setCreateNew(accessible[0][1]));
      }
    }

    setTimeout(() => {
      if (nextValue && projectNameInputRef.current) {
        projectNameInputRef.current.focus();
      }
    }, 0);
  };

  const handleCreateProject = async () => {
    if (!user || !setSelectedFile || !user.projectdigest) return;

    const projectExists = Object.keys(user.projectdigest).findIndex((project) => project === projectName);

    if (projectExists !== -1) {
      alert(t("project_name_already_exists"));
      return;
    }

    await createNewProject(projectName);

    if (setSelectedProjectName) {
      await getUser();
      setSelectedProjectName(projectName);
      dispatch(setCreateNew(null));
      setProjectName("");
      setShowCreateNewProject(false);
    }
  };

  useEffect(() => {
    if (user && setSelectedFile && projectName !== "" && fileName !== "" && user.projectdigest) {
      let index = user.projectdigest[projectName].files.findIndex((p: ProjectFile) => p.name === fileName);
      if (index !== -1) {
        setSelectedFile(index);
        if (setCopyOfCurrentSelectedFile)
          setCopyOfCurrentSelectedFile({ ...user.projectdigest[projectName].files[index] });
        setFileName("");
        setProjectName("");
        setTemplate(null);
        setMtrlTemplate(null);
        dispatch(setCreateNew(null));
      }
    }
  }, [user, projectName, fileName, setSelectedFile, setCopyOfCurrentSelectedFile, dispatch]);

  // Leave edit mode if the title we're editing changes from under us (e.g. a different project
  // got selected) so we don't end up editing the wrong project's name.
  useEffect(() => {
    setIsEditingTitle(false);
  }, [headerTitle, isFileList]);

  const canRenameTitle = Boolean(isFileList && isDevConfig);

  const handleRenameCommit = (newValue: string) => {
    const trimmed = newValue.trim();
    setIsEditingTitle(false);
    if (!trimmed || trimmed === headerTitle) return;
    onRenameProject?.(trimmed);
  };

  return (
    <div
      id={id}
      className={["header-toolbar", className].filter(Boolean).join(" ")}
      style={{ width: "100%", margin: 0 }}
    >
      <Row className="header-bg-list justify-content-start align-items-center">
        <Col xs={isFileList ? 12 : 6} className={isFileList ? "ps-2" : "ps-2"}>
          {isEditingTitle ? (
            <Row className="ps-3 mb-1">
              <Col xs={3}>
                <SXEditField
                  id="header-title-edit"
                  type="text"
                  fontSize={14}
                  textAlign="left"
                  border
                  val={headerTitle || ""}
                  onUpdate={(newValue) => handleRenameCommit(String(newValue))}
                />
              </Col>
              <Col xs={6}>
                <Row>
                  <Col>
                    <ButtonGroup>
                      <button
                        id="header-title-save"
                        className="primary-btn"
                        disabled={!headerTitle || !headerTitle.trim()}
                        onClick={() => handleRenameCommit(headerTitle || "")}
                      >
                        {t("save")}
                      </button>

                      {/** cancel button */}
                      <button
                        id="header-title-cancel"
                        className="secondary-btn"
                        onClick={() => setIsEditingTitle(false)}
                      >
                        {t("cancel")}
                      </button>
                    </ButtonGroup>
                  </Col>
                </Row>
              </Col>
            </Row>
          ) : (
            <span className="header-title" onClick={() => setIsEditingTitle(true)}>
              {headerTitle}
              {canRenameTitle && (
                <span className="pointer ms-2">
                  <EditIcon size={24} />
                </span>
              )}
            </span>
          )}
        </Col>
        <Col xs={isFileList ? 12 : 6} className="">
          <HeaderToolbarActions
            showStatusDropdown={showStatusDropdown}
            statusValue={statusValue}
            onStatusChange={onStatusChange}
            disableFileActions={disableFileActions}
            isInProjectlist={isInProjectlist}
            buttonTitle={buttonTitle}
            onToggleCreate={handleToggleCreate}
            uploadInputRef={uploadInputRef}
            uploadProjectFile={uploadProjectFile}
            renderToolbarActions={renderToolbarActions}
            handleDelete={handleDelete}
            t={t}
          />
        </Col>
      </Row>

      <HeaderToolbarCreateProjectForm
        show={Boolean(showCreateNewProject)}
        isInProjectlist={isInProjectlist}
        user={user}
        projectName={projectName}
        onProjectNameChange={setProjectName}
        projectNameInputRef={projectNameInputRef}
        isDuplicateProjectName={isDuplicateProjectName}
        isCreateDisabled={!projectName || isDuplicateProjectName}
        onCancel={resetCreateProjectForm}
        onCreate={handleCreateProject}
        t={t}
      />
    </div>
  );
}

export default HeaderToolbar;
